import { useCallback, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Betriebsstellenart } from '../types'
import useCaptureException from './useCaptureException'
import { useDebounce } from './useDebounce'
import { BetriebsstelleSearchAPI } from '../contexts'

interface UseBetriebsstelleAutocompleteOptions {
  art?: Betriebsstellenart | null
  minLength?: number
  debounceMs?: number
  searchAPI?: BetriebsstelleSearchAPI
}

interface AutocompleteState {
  activeSuggestionIndex: number
  inputValue: string
  showDropdown: boolean
}

/**
 * Hook for Betriebsstelle autocomplete functionality.
 */
export function useBetriebsstelleAutocomplete({
  art,
  minLength = 0,
  debounceMs = 250,
  searchAPI
}: UseBetriebsstelleAutocompleteOptions = {}) {
  const { captureException } = useCaptureException()

  const [state, setState] = useState<AutocompleteState>({
    activeSuggestionIndex: -1,
    inputValue: '',
    showDropdown: false
  })

  const debouncedQuery = useDebounce(state.inputValue, debounceMs)
  const shouldFetch = Boolean(
    searchAPI?.isBetriebsstellenApiEnabled() &&
    debouncedQuery.length >= minLength &&
    debouncedQuery.trim()
  )

  const {
    data: suggestions = [],
    isFetching,
    isError,
    error
  } = useQuery({
    queryKey: ['betriebsstelle-search', debouncedQuery, art] as const,
    queryFn: async ({ signal }) => {
      if (!searchAPI) throw new Error('Search API not available')

      const response = await searchAPI.getBetriebsstellenSearch(
        debouncedQuery,
        art ? [art] : undefined,
        signal
      )
      return response.map((entry) => entry.rl100Langname)
    },
    enabled: shouldFetch,
    staleTime: 0,
    gcTime: 0
  })

  if (isError && error && !searchAPI?.isCancel(error)) {
    captureException(error, 'betriebsstelle-autocomplete', {
      query: debouncedQuery,
      typ: art,
      minLength
    })
  }

  const handleInputChange = useCallback((value: string) => {
    setState((prev) => ({
      ...prev,
      inputValue: value,
      activeSuggestionIndex: -1,
      showDropdown: value.length > 0
    }))
  }, [])

  const setActiveSuggestionIndex = useCallback((index: number) => {
    setState((prev) => ({ ...prev, activeSuggestionIndex: index }))
  }, [])

  const resetSuggestions = useCallback(() => {
    setState((prev) => ({
      ...prev,
      activeSuggestionIndex: -1,
      showDropdown: false
    }))
  }, [])

  const setInputValue = useCallback((value: string) => {
    setState((prev) => ({
      ...prev,
      inputValue: value,
      showDropdown: false,
      activeSuggestionIndex: -1
    }))
  }, [])

  const openDropDown = useCallback(() => {
    setState((prev) => ({ ...prev, showDropdown: true }))
  }, [])

  const shouldShowSuggestions = state.showDropdown && suggestions.length > 0

  return {
    filteredSuggestions: shouldShowSuggestions ? suggestions : [],
    activeSuggestionIndex: state.activeSuggestionIndex,
    isLoading: isFetching,
    isEnabled: Boolean(searchAPI?.isBetriebsstellenApiEnabled()),
    inputValue: state.inputValue,
    handleInputChange,
    setActiveSuggestionIndex,
    resetFilteredSuggestions: resetSuggestions,
    setInputValue,
    openDropDown
  }
}
